import React, { useState, useEffect, useCallback, useRef } from 'react';
import { vendutoApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  Loader2, Search, X, Calculator, Save, Trash2, Sparkles, AlertTriangle, Bookmark, Package, Tags,
} from 'lucide-react';
import VendutoHeader from '../components/venduto/VendutoHeader';
import PeriodPicker from '../components/venduto/PeriodPicker';
import { useVendutoPeriodo } from '../hooks/useVendutoPeriodo';
import { periodoRange, fmtEuro, fmtNum, fmtPct, SERVIZI_FILTRO } from '../lib/venduto';

const VendutoQueryPage = () => {
  const [stato, setStato] = useState(null);
  const [sel, setSel] = useVendutoPeriodo(stato);
  const [servizio, setServizio] = useState('tutti');

  const [testo, setTesto] = useState('');
  const [suggerimenti, setSuggerimenti] = useState(null);
  const [cercando, setCercando] = useState(false);
  const [selezione, setSelezione] = useState({ prodotti: [], categorie: [] });

  const [risultato, setRisultato] = useState(null);
  const [calcolando, setCalcolando] = useState(false);

  const [panieri, setPanieri] = useState([]);
  const [salvaOpen, setSalvaOpen] = useState(false);
  const [nomePaniere, setNomePaniere] = useState('');

  const [domanda, setDomanda] = useState('');
  const [interpretando, setInterpretando] = useState(false);
  const [proposta, setProposta] = useState(null);

  const debounce = useRef(null);

  useEffect(() => {
    vendutoApi.getStato().then(setStato).catch(() => {});
    vendutoApi.getPanieri().then(setPanieri).catch(() => {});
  }, []);

  const range = sel && stato ? periodoRange(sel, stato) : null;

  // Autocompletamento sui nomi realmente presenti in archivio
  useEffect(() => {
    if (!range) return;
    if (debounce.current) clearTimeout(debounce.current);
    if (!testo.trim()) {
      setSuggerimenti(null);
      return;
    }
    debounce.current = setTimeout(async () => {
      setCercando(true);
      try {
        setSuggerimenti(await vendutoApi.getVoci(range.data_inizio, range.data_fine, testo.trim()));
      } catch (e) {
        setSuggerimenti(null);
      } finally {
        setCercando(false);
      }
    }, 250);
    return () => debounce.current && clearTimeout(debounce.current);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [testo, range?.data_inizio, range?.data_fine]);

  const aggiungi = (tipo, valore) => {
    setSelezione((s) => {
      const key = tipo === 'prodotto' ? 'prodotti' : 'categorie';
      if (s[key].includes(valore)) return s;
      return { ...s, [key]: [...s[key], valore] };
    });
    setTesto('');
    setSuggerimenti(null);
  };

  const rimuovi = (tipo, valore) => {
    const key = tipo === 'prodotto' ? 'prodotti' : 'categorie';
    setSelezione((s) => ({ ...s, [key]: s[key].filter((v) => v !== valore) }));
  };

  const calcola = useCallback(async (override) => {
    const selez = override || selezione;
    if (!range) return;
    if (!selez.prodotti.length && !selez.categorie.length) {
      toast.error('Seleziona almeno un prodotto o una categoria');
      return;
    }
    setCalcolando(true);
    try {
      const res = await vendutoApi.query({
        data_inizio: range.data_inizio,
        data_fine: range.data_fine,
        prodotti: selez.prodotti,
        categorie: selez.categorie,
        servizio,
      });
      setRisultato(res);
      if (res.non_trovate?.length) {
        toast.warning(`Non trovate nel periodo: ${res.non_trovate.map((n) => n.valore).join(', ')}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel calcolo');
    } finally {
      setCalcolando(false);
    }
  }, [selezione, range, servizio]);

  const salvaPaniere = async () => {
    try {
      const p = await vendutoApi.createPaniere({
        nome: nomePaniere,
        prodotti: selezione.prodotti,
        categorie: selezione.categorie,
        servizio,
      });
      setPanieri((list) => [...list, p]);
      setSalvaOpen(false);
      setNomePaniere('');
      toast.success('Paniere salvato');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const caricaPaniere = (p) => {
    setSelezione({ prodotti: p.prodotti || [], categorie: p.categorie || [] });
    setServizio(p.servizio || 'tutti');
    setRisultato(null);
    toast.success(`Paniere "${p.nome}" caricato`);
  };

  const eliminaPaniere = async (id) => {
    try {
      await vendutoApi.deletePaniere(id);
      setPanieri((list) => list.filter((p) => p.id !== id));
    } catch (e) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const interpreta = async () => {
    if (!domanda.trim() || !range) return;
    setInterpretando(true);
    setProposta(null);
    try {
      const res = await vendutoApi.queryNl({
        domanda,
        data_inizio: range.data_inizio,
        data_fine: range.data_fine,
      });
      setProposta(res);
      if (res.vuota) toast.warning('Non ho trovato voci corrispondenti: prova a riformulare');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Il modulo AI non ha risposto');
    } finally {
      setInterpretando(false);
    }
  };

  const confermaProposta = () => {
    const nuova = { prodotti: proposta.prodotti || [], categorie: proposta.categorie || [] };
    setSelezione(nuova);
    if (proposta.servizio) setServizio(proposta.servizio);
    setProposta(null);
    calcola(nuova);
  };

  const nSelezionate = selezione.prodotti.length + selezione.categorie.length;

  return (
    <div className="min-h-screen pb-24">
      <VendutoHeader titolo="Query sul venduto" sottotitolo={range?.label} />

      <main className="px-4 py-5 max-w-lg mx-auto space-y-4">
        <Card>
          <CardContent className="p-4 space-y-3">
            {stato && sel && <PeriodPicker value={sel} onChange={setSel} archivio={stato} />}
            <Select value={servizio} onValueChange={setServizio}>
              <SelectTrigger className="h-10 w-full" data-testid="query-servizio">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SERVIZI_FILTRO.map((s) => (
                  <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Ricerca voci */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Search className="w-4 h-4 text-primary" /> Cerca prodotti e categorie
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Input
                value={testo}
                onChange={(e) => setTesto(e.target.value)}
                placeholder="Es. tagliata, burger, pizze…"
                className="h-12 pr-10"
                data-testid="query-search-input"
              />
              {cercando && <Loader2 className="w-4 h-4 animate-spin absolute right-3 top-4 text-muted-foreground" />}
            </div>

            {suggerimenti && (
              <div className="border rounded-lg divide-y max-h-72 overflow-y-auto" data-testid="query-suggerimenti">
                {suggerimenti.categorie.map((c) => (
                  <button
                    key={`c-${c.valore}`}
                    className="w-full flex items-center justify-between gap-2 p-2.5 text-left hover:bg-secondary"
                    onClick={() => aggiungi('categoria', c.valore)}
                    data-testid={`sugg-cat-${c.valore}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Tags className="w-4 h-4 text-primary shrink-0" />
                      <span className="text-sm truncate">{c.nome}</span>
                      <Badge variant="secondary" className="text-[10px] shrink-0">categoria · {c.n_prodotti} prod.</Badge>
                    </span>
                    <span className="text-xs font-mono-numbers text-muted-foreground shrink-0">{fmtEuro(c.fatturato)}</span>
                  </button>
                ))}
                {suggerimenti.prodotti.map((p) => (
                  <button
                    key={`p-${p.valore}`}
                    className="w-full flex items-center justify-between gap-2 p-2.5 text-left hover:bg-secondary"
                    onClick={() => aggiungi('prodotto', p.valore)}
                    data-testid={`sugg-prod-${p.valore}`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <Package className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="text-sm truncate">{p.nome}</span>
                    </span>
                    <span className="text-xs font-mono-numbers text-muted-foreground shrink-0">
                      {fmtNum(p.quantita, 0)} pz
                    </span>
                  </button>
                ))}
                {suggerimenti.prodotti.length === 0 && suggerimenti.categorie.length === 0 && (
                  <p className="p-3 text-sm text-muted-foreground italic">
                    Nessuna voce con questo nome nel periodo caricato.
                  </p>
                )}
              </div>
            )}

            {nSelezionate > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="query-selezione">
                {selezione.categorie.map((c) => (
                  <Badge key={c} className="gap-1 bg-primary/10 text-primary border-primary/30">
                    <Tags className="w-3 h-3" /> {c}
                    <button onClick={() => rimuovi('categoria', c)} data-testid={`rimuovi-cat-${c}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
                {selezione.prodotti.map((p) => (
                  <Badge key={p} variant="secondary" className="gap-1">
                    <Package className="w-3 h-3" /> {p}
                    <button onClick={() => rimuovi('prodotto', p)} data-testid={`rimuovi-prod-${p}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}

            <div className="flex gap-2">
              <Button
                className="flex-1 h-12 font-bold"
                onClick={() => calcola()}
                disabled={calcolando || nSelezionate === 0}
                data-testid="calcola-btn"
              >
                {calcolando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
                Calcola venduto
              </Button>
              <Button
                variant="outline"
                className="h-12"
                onClick={() => setSalvaOpen(true)}
                disabled={nSelezionate === 0}
                data-testid="salva-paniere-btn"
              >
                <Save className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Risultato */}
        {risultato && !risultato.vuoto && (
          <Card className="border-primary/40" data-testid="query-risultato">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Risultato</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-xl font-bold font-mono-numbers text-primary" data-testid="risultato-fatturato">
                    {fmtEuro(risultato.fatturato)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Fatturato</p>
                </div>
                <div>
                  <p className="text-xl font-bold font-mono-numbers" data-testid="risultato-quantita">
                    {fmtNum(risultato.quantita, 0)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Quantità</p>
                </div>
                <div>
                  <p className="text-xl font-bold font-mono-numbers" data-testid="risultato-quota">
                    {fmtPct(risultato.quota)}
                  </p>
                  <p className="text-[11px] text-muted-foreground">Sul fatturato periodo</p>
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Per voce</p>
                <div className="divide-y">
                  {risultato.dettaglio.map((d) => (
                    <div key={`${d.tipo}-${d.valore}`} className="flex items-center justify-between py-2 text-xs">
                      <span className="flex items-center gap-2 min-w-0">
                        {d.tipo === 'categoria' ? <Tags className="w-3 h-3 text-primary" /> : <Package className="w-3 h-3" />}
                        <span className="truncate">{d.nome}</span>
                        {d.n_prodotti && <span className="text-muted-foreground">({d.n_prodotti} prod.)</span>}
                      </span>
                      <span className="font-mono-numbers shrink-0">
                        {fmtNum(d.quantita, 0)} pz · {fmtEuro(d.fatturato)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Per servizio</p>
                <div className="divide-y">
                  {risultato.per_servizio.map((s) => (
                    <div key={s.servizio} className="flex items-center justify-between py-2 text-xs">
                      <span>{s.label}</span>
                      <span className="font-mono-numbers">{fmtNum(s.quantita, 0)} pz · {fmtEuro(s.fatturato)}</span>
                    </div>
                  ))}
                </div>
              </div>

              {risultato.sovrapposizioni?.length > 0 && (
                <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900 flex gap-2">
                  <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                  <p>
                    Alcuni prodotti selezionati appartengono anche a una categoria selezionata
                    ({risultato.sovrapposizioni.slice(0, 5).join(', ')}): nel totale sono contati una sola volta,
                    nel dettaglio per voce compaiono in entrambe le righe.
                  </p>
                </div>
              )}

              {risultato.non_trovate?.length > 0 && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-[11px] text-red-900">
                  Voci non presenti nel periodo: {risultato.non_trovate.map((n) => n.valore).join(', ')}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {risultato?.vuoto && (
          <Card className="border-amber-300 bg-amber-50">
            <CardContent className="p-4 text-sm text-amber-900">
              Nessun venduto per questa selezione nel periodo e servizio scelti.
            </CardContent>
          </Card>
        )}

        {/* Query in linguaggio naturale */}
        <Card className="border-dashed">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" /> Chiedi a parole
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Scrivi la domanda in italiano: l'assistente propone quali voci includere, tu confermi e
              il calcolo lo fa il server sui dati veri.
            </p>
            <Textarea
              value={domanda}
              onChange={(e) => setDomanda(e.target.value)}
              placeholder="Es. quanto abbiamo venduto di tagliate, burger e fritti a cena?"
              rows={3}
              data-testid="nl-domanda"
            />
            <Button
              variant="outline"
              className="w-full h-11"
              onClick={interpreta}
              disabled={interpretando || !domanda.trim()}
              data-testid="nl-interpreta-btn"
            >
              {interpretando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Interpreta la domanda
            </Button>

            {proposta && (
              <div className="p-3 rounded-lg border bg-secondary/60 space-y-2" data-testid="nl-proposta">
                <p className="text-xs font-semibold">Ho capito così:</p>
                {proposta.spiegazione && <p className="text-xs text-muted-foreground">{proposta.spiegazione}</p>}
                <div className="flex flex-wrap gap-1">
                  {proposta.categorie?.map((c) => (
                    <Badge key={c} className="bg-primary/10 text-primary border-primary/30 text-[10px]">{c}</Badge>
                  ))}
                  {proposta.prodotti?.map((p) => (
                    <Badge key={p} variant="secondary" className="text-[10px]">{p}</Badge>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  Servizio: {SERVIZI_FILTRO.find((s) => s.key === proposta.servizio)?.label || 'Tutti i servizi'}
                </p>
                {proposta.non_trovate?.length > 0 && (
                  <p className="text-[11px] text-red-700">
                    Non presenti in archivio: {proposta.non_trovate.join(', ')}
                  </p>
                )}
                <div className="flex gap-2 pt-1">
                  <Button variant="ghost" size="sm" onClick={() => setProposta(null)}>Annulla</Button>
                  <Button size="sm" onClick={confermaProposta} disabled={proposta.vuota} data-testid="nl-conferma-btn">
                    Conferma e calcola
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Panieri salvati */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Bookmark className="w-4 h-4 text-primary" /> Panieri salvati
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2" data-testid="panieri-card">
            {panieri.length === 0 && (
              <p className="text-sm text-muted-foreground italic">
                Nessun paniere: salva una combinazione ricorrente col pulsante accanto a "Calcola".
              </p>
            )}
            {panieri.map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border">
                <button className="min-w-0 text-left flex-1" onClick={() => caricaPaniere(p)} data-testid={`paniere-${p.id}`}>
                  <p className="text-sm font-medium truncate">{p.nome}</p>
                  <p className="text-[11px] text-muted-foreground truncate">
                    {[...(p.categorie || []), ...(p.prodotti || [])].join(', ')}
                  </p>
                </button>
                <Button variant="ghost" size="icon" onClick={() => eliminaPaniere(p.id)} data-testid={`elimina-paniere-${p.id}`}>
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </main>

      <Dialog open={salvaOpen} onOpenChange={setSalvaOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Salva paniere</DialogTitle>
          </DialogHeader>
          <Input
            value={nomePaniere}
            onChange={(e) => setNomePaniere(e.target.value)}
            placeholder="Es. Secondi + Fritti"
            className="h-12"
            data-testid="nome-paniere-input"
          />
          <p className="text-xs text-muted-foreground">
            {selezione.categorie.length} categorie e {selezione.prodotti.length} prodotti selezionati.
          </p>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSalvaOpen(false)}>Annulla</Button>
            <Button onClick={salvaPaniere} disabled={!nomePaniere.trim()} data-testid="conferma-salva-paniere">
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendutoQueryPage;
